<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[130] flex items-end justify-center">
      <div class="absolute inset-0 bg-black/60" @click="close" />

      <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl shadow-2xl flex flex-col sheet-inset"
           style="height: 85vh">

        <!-- Состояние распознавания этикетки (полноэкранное, как в LabelReviewDialog) -->
        <template v-if="state === 'label-recognizing'">
          <div class="flex-1 flex flex-col" style="padding: 24px 24px 28px;">
            <!-- Кнопка закрытия -->
            <div class="flex justify-end shrink-0">
              <button @click="close" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Фото с эффектом сканирования + заголовок -->
            <div class="flex-1 flex flex-col items-center justify-center" style="gap: 32px;">
              <div class="relative overflow-hidden" style="width: 220px; height: 220px; border-radius: 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.10);">
                <img v-if="labelPhotoUrl" :src="labelPhotoUrl" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full bg-line" />
                <div class="scan-overlay" />
                <div class="scan-corner" style="top:12px;left:12px;border-top:2px solid rgba(255,255,255,0.9);border-left:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="top:12px;right:12px;border-top:2px solid rgba(255,255,255,0.9);border-right:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="bottom:12px;left:12px;border-bottom:2px solid rgba(255,255,255,0.9);border-left:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="bottom:12px;right:12px;border-bottom:2px solid rgba(255,255,255,0.9);border-right:2px solid rgba(255,255,255,0.9);" />
              </div>

              <div class="text-center">
                <p style="font-weight: 700; font-size: var(--fs-22); color: var(--ink); letter-spacing: -0.2px; margin-bottom: 6px;">Распознаю этикетку</p>
                <p style="font-size: var(--fs-13); color: var(--ink-70);">Обычно 3–15 сек</p>
              </div>
            </div>
          </div>
        </template>

        <!-- Остальные состояния: заголовок + контент -->
        <template v-else>

        <!-- Заголовок -->
        <div class="px-5 pt-5 pb-2 shrink-0 flex items-center justify-between">
          <h2 style="font-family: Inter, sans-serif; font-size: var(--fs-20); font-weight: 700; color: var(--ink);">
            {{ headerTitle }}
          </h2>
          <button @click="close" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 pb-6">

          <!-- ══ SCANNING ══ -->
          <div v-if="state === 'scanning'" class="flex flex-col items-center gap-4">
            <div class="relative w-full rounded-2xl overflow-hidden bg-black" style="aspect-ratio: 4/3">
              <div id="barcode-reader" class="w-full h-full"></div>
              <!-- Прицел -->
              <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div class="border-2 border-white/70 rounded-xl" style="width: 90%; height: 50%; box-shadow: 0 0 0 9999px rgba(0,0,0,0.3)"></div>
              </div>
              <!-- Подсказка фокусировки (iOS) -->
              <div v-if="showFocusHint" class="absolute inset-0 flex items-end justify-center pb-3 pointer-events-none">
                <span class="text-white/90 text-14 bg-black/50 px-4 py-1.5 rounded-2xl backdrop-blur-sm text-center leading-snug">
                  Приблизьте камеру вплотную к штрихкоду<br/>и медленно отведите назад
                </span>
              </div>
            </div>

            <p class="text-15 text-ink/70 text-center">Наведите камеру на штрихкод</p>
            <p v-if="scanError" class="text-14 text-red-500 text-center">{{ scanError }}</p>

            <!-- Сфотографировать штрихкод (нативная камера, без запроса разрешений) -->
            <PhotoPicker
              :disabled="barcodePhotoLoading"
              button-class="h-11 rounded-xl bg-white text-14 font-medium text-ink border border-[#E0D9CF] active:bg-[#F5EFEA] disabled:opacity-50 flex items-center justify-center gap-2"
              camera-denied-text="Для съёмки штрихкода нужен доступ к камере. Разрешите доступ в настройках браузера."
              @file="onBarcodePhoto"
            >
              <template v-if="barcodePhotoLoading">
                <span class="inline-block w-4 h-4 border-2 border-ink/30 border-t-[#41291A] rounded-full animate-spin" />
                Распознаю...
              </template>
              <template v-else>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Сфотографировать штрихкод
              </template>
            </PhotoPicker>
            <p v-if="barcodePhotoError" class="text-14 text-red-500 text-center -mt-2">{{ barcodePhotoError }}</p>
            <!-- Скрытый контейнер для scanFile -->
            <div id="barcode-photo-temp" class="hidden"></div>

            <!-- Ручной ввод штрихкода -->
            <button @click="showManualInput = !showManualInput" class="text-14 text-ink/70 underline">
              Ввести код вручную
            </button>
            <div v-if="showManualInput" class="w-full flex gap-2">
              <input
                v-model="manualCode"
                type="text"
                inputmode="numeric"
                placeholder="Штрихкод (EAN-13)"
                maxlength="13"
                class="flex-1 h-11 px-4 rounded-xl bg-white text-15 text-ink outline-none border border-[#E0D9CF] focus:border-ink"
              />
              <button
                @click="submitManualCode"
                :disabled="manualCode.length < 8"
                class="h-11 px-5 rounded-xl bg-ink text-white text-14 font-semibold disabled:opacity-40 active:bg-[#2E1D12]"
              >
                Найти
              </button>
            </div>
          </div>

          <!-- ══ SEARCHING ══ -->
          <div v-else-if="state === 'searching'" class="flex flex-col items-center justify-center gap-4 py-12">
            <div class="w-10 h-10 border-3 border-ink/20 border-t-[#41291A] rounded-full animate-spin"></div>
            <p class="text-15 text-ink/70">Ищем продукт: {{ scannedCode }}</p>
          </div>

          <!-- ══ FOUND ══ -->
          <div v-else-if="state === 'found'" class="flex flex-col gap-4">
            <!-- Добавить фото упаковки (если нет) -->
            <div v-if="!foundProduct.image_url && !coverPreviewUrl" class="bg-white rounded-2xl p-4 shadow-sm">
              <PhotoPicker
                button-class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-sand text-ink/70 active:bg-[#F5EFEA] transition-colors"
                gallery-class="rounded-xl border-2 border-dashed border-sand text-ink/70 active:bg-[#F5EFEA] transition-colors"
                camera-denied-text="Для съёмки упаковки нужен доступ к камере. Разрешите доступ в настройках браузера."
                @file="onCoverPhoto"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span class="text-14 font-medium">Добавить фото упаковки</span>
              </PhotoPicker>
              <p class="text-14 text-ink/70 mt-1.5 text-center">Фото будет видно всем пользователям</p>
            </div>
            <!-- Превью загруженного фото -->
            <div v-else-if="coverPreviewUrl && !foundProduct.image_url" class="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3">
              <img :src="coverPreviewUrl" class="w-16 h-16 rounded-xl object-cover" />
              <div class="flex-1">
                <p class="text-14 text-ink/70">Фото упаковки добавлено</p>
              </div>
              <button @click="coverPreviewUrl = null; coverBase64 = null; coverPhotoUrl = null" class="text-ink/60 active:text-ink">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Карточка продукта -->
            <div class="bg-white rounded-2xl p-4 shadow-sm">
              <div class="flex items-start gap-3">
                <img v-if="foundProduct.image_url || coverPhotoUrl" :src="imgUrl(foundProduct.image_url || coverPreviewUrl || coverPhotoUrl)" class="w-16 h-16 rounded-xl object-cover shrink-0" />
                <div class="flex-1 min-w-0">
                  <p class="text-16 font-semibold text-ink leading-tight">{{ foundProduct.name }}</p>
                  <p v-if="foundProduct.brand" class="text-14 text-ink/70 mt-0.5">{{ foundProduct.brand }}</p>
                  <p class="text-14 text-ink/70 mt-1">{{ scannedCode }}</p>
                </div>
              </div>

              <!-- КБЖУ на 100 г -->
              <div class="flex items-center gap-2 mt-3">
                <div class="flex gap-2 flex-wrap flex-1">
                  <span class="pill-kcal text-14 font-medium">{{ fmtVal(foundProduct.kcal) }} ккал</span>
                  <span class="pill-protein text-14 font-medium">Б {{ fmtVal(foundProduct.protein) }}</span>
                  <span class="pill-fat text-14 font-medium">Ж {{ fmtVal(foundProduct.fat) }}</span>
                  <span class="pill-carbs text-14 font-medium">У {{ fmtVal(foundProduct.carbs) }}</span>
                </div>
                <!-- Флаг — коррекция продукта -->
                <button
                  @click="openCorrectionDialog"
                  class="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-ink/70 active:bg-[#E0D9CF] transition-colors"
                  title="Исправить данные"
                >
                  <svg width="15" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                    <line x1="4" y1="22" x2="4" y2="15"/>
                  </svg>
                </button>
              </div>
              <p class="text-14 font-medium text-ink/70 mt-1">на 100 г</p>
            </div>

            <!-- Вес порции (не нужен в selectOnly — возвращаем только продукт) -->
            <div v-if="!selectOnly" class="bg-white rounded-2xl p-4 shadow-sm">
              <label class="text-14 font-semibold text-ink">Вес порции, г</label>
              <input
                :value="portionWeightStr"
                @input="onPortionWeightInput"
                type="text"
                inputmode="decimal"
                class="mt-2 w-full h-11 px-4 rounded-xl bg-[#F5EFEA] text-16 text-ink outline-none focus:ring-2 focus:ring-ink/20"
              />
              <!-- КБЖУ на порцию -->
              <div v-if="portionWeight > 0" class="flex gap-2 mt-3 flex-wrap">
                <span class="pill-kcal text-14 font-medium">{{ fmtVal(calcPortion(foundProduct.kcal)) }} ккал</span>
                <span class="pill-protein text-14 font-medium">Б {{ fmtVal(calcPortion(foundProduct.protein)) }}</span>
                <span class="pill-fat text-14 font-medium">Ж {{ fmtVal(calcPortion(foundProduct.fat)) }}</span>
                <span class="pill-carbs text-14 font-medium">У {{ fmtVal(calcPortion(foundProduct.carbs)) }}</span>
              </div>
              <p v-if="portionWeight > 0" class="text-14 font-medium text-ink/70 mt-1">на {{ portionWeight }} г</p>
            </div>

            <!-- Кнопка добавить / выбрать -->
            <button
              v-if="selectOnly"
              @click="selectAndClose"
              class="w-full h-12 rounded-2xl text-white font-semibold text-16 bg-ink active:bg-[#2E1D12] transition-colors"
            >
              Выбрать продукт
            </button>
            <button
              v-else
              @click="addToDiary"
              :disabled="addingToDiary || portionWeight <= 0"
              class="w-full h-12 rounded-2xl text-white font-semibold text-16 transition-colors"
              :class="addingToDiary ? 'bg-ink/50' : 'bg-ink active:bg-[#2E1D12]'"
            >
              {{ addingToDiary ? 'Добавляем...' : 'Добавить в дневник' }}
            </button>

            <!-- Кнопка сканировать другой -->
            <button @click="resetToScan" class="text-14 text-ink/70 underline mx-auto">
              Сканировать другой
            </button>
          </div>

          <!-- ══ NOT FOUND ══ -->
          <div v-else-if="state === 'not-found'" class="flex flex-col items-center gap-5 py-6">
            <div class="w-16 h-16 rounded-full bg-kcal/20 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EE998C" stroke-width="2" stroke-linecap="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </div>
            <div class="text-center">
              <p class="text-16 font-semibold text-ink">
                <template v-if="incompleteProductName">Продукт найден, но КБЖУ отсутствуют</template>
                <template v-else-if="lookupUnavailable">База продуктов временно недоступна</template>
                <template v-else>Этого штрихкода пока нет в базе данных</template>
              </p>
              <p v-if="incompleteProductName" class="text-14 text-ink/70 mt-1">{{ incompleteProductName }}</p>
              <p class="text-14 text-ink/70 mt-2 leading-snug">
                <template v-if="lookupUnavailable">
                  Не смогли проверить штрихкод — внешний справочник не отвечает. Попробуйте
                  повторить через пару минут или сфотографируйте этикетку прямо сейчас.
                </template>
                <template v-else>
                  Сфотографируйте состав и КБЖУ на этикетке — мы добавим продукт для вас и всех пользователей Зои.
                </template>
              </p>
            </div>

            <button
              v-if="lookupUnavailable"
              @click="retryLookup"
              :disabled="retryingLookup"
              class="w-full h-12 rounded-2xl bg-ink text-white font-semibold text-16 active:bg-[#2E1D12] transition-colors disabled:opacity-60"
            >
              {{ retryingLookup ? 'Проверяем...' : 'Повторить поиск' }}
            </button>

            <PhotoPicker
              :button-class="'h-12 rounded-2xl font-semibold text-16 transition-colors flex items-center justify-center gap-2 ' + (lookupUnavailable
                ? 'bg-white text-ink border border-line active:bg-[#F5EFEA]'
                : 'bg-ink text-white active:bg-[#2E1D12]')"
              gallery-class="rounded-2xl border border-line bg-white text-ink active:bg-[#F5EFEA] transition-colors"
              camera-denied-text="Для съёмки этикетки нужен доступ к камере. Разрешите доступ в настройках браузера."
              @file="onLabelPhoto"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              Сфотографировать
            </PhotoPicker>

            <p v-if="scanError" class="text-14 text-red-500 text-center">{{ scanError }}</p>

            <button @click="resetToScan" class="text-14 text-ink/70 underline">
              Сканировать другой
            </button>
          </div>

          <!-- ══ REVIEW ══ -->
          <div v-else-if="state === 'review'" class="flex flex-col gap-4">
            <p class="text-14 text-ink/70">Проверьте данные с этикетки</p>

            <!-- Фото упаковки -->
            <div class="bg-white rounded-2xl p-4 shadow-sm">
              <div v-if="!coverPreviewUrl" class="flex flex-col items-center">
                <PhotoPicker
                  class="w-full"
                  button-class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-sand text-ink/70 active:bg-[#F5EFEA] transition-colors"
                  gallery-class="rounded-xl border-2 border-dashed border-sand text-ink/70 active:bg-[#F5EFEA] transition-colors"
                  camera-denied-text="Для съёмки упаковки нужен доступ к камере. Разрешите доступ в настройках браузера."
                  @file="onCoverPhoto"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span class="text-14 font-medium">Добавить фото упаковки</span>
                </PhotoPicker>
                <p class="text-14 text-ink/70 mt-1.5">Фото будет на обложке продукта</p>
              </div>
              <div v-else class="flex items-center gap-3">
                <img :src="coverPreviewUrl" class="w-16 h-16 rounded-xl object-cover" />
                <div class="flex-1">
                  <p class="text-14 text-ink/70">Фото упаковки</p>
                </div>
                <button @click="coverPhotoUrl = null; coverPreviewUrl = null; coverBase64 = null" class="text-ink/60 active:text-ink">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="bg-white rounded-2xl p-4 shadow-sm flex flex-col gap-3">
              <div>
                <label class="text-14 text-ink/70 font-medium">Название</label>
                <input v-model="reviewData.name" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
              </div>
              <div>
                <label class="text-14 text-ink/70 font-medium">Бренд</label>
                <input v-model="reviewData.brand" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="text-14 text-ink/70 font-medium">Ккал / 100 г</label>
                  <input :value="reviewData.kcal" @input="reviewData.kcal = parseDecimal($event.target.value)" type="text" inputmode="decimal" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
                </div>
                <div>
                  <label class="text-14 text-ink/70 font-medium">Белки / 100 г</label>
                  <input :value="reviewData.protein" @input="reviewData.protein = parseDecimal($event.target.value)" type="text" inputmode="decimal" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
                </div>
                <div>
                  <label class="text-14 text-ink/70 font-medium">Жиры / 100 г</label>
                  <input :value="reviewData.fat" @input="reviewData.fat = parseDecimal($event.target.value)" type="text" inputmode="decimal" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
                </div>
                <div>
                  <label class="text-14 text-ink/70 font-medium">Углеводы / 100 г</label>
                  <input :value="reviewData.carbs" @input="reviewData.carbs = parseDecimal($event.target.value)" type="text" inputmode="decimal" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1" />
                </div>
              </div>

              <!-- Клетчатка свёрнута: маркировка её не требует, поле пустовало
                   бы чаще, чем заполнялось. Раскрывается само, если Зоя
                   распознала волокна на этикетке. -->
              <button
                v-if="!fiberOpen"
                type="button"
                @click="fiberOpen = true"
                class="text-14 text-ink/70 font-medium underline underline-offset-2 text-left"
              >
                Указать клетчатку
              </button>
              <div v-else>
                <label class="text-14 text-ink/70 font-medium">Клетчатка / 100 г</label>
                <input :value="reviewData.fiber" @input="reviewData.fiber = parseDecimal($event.target.value)" type="text" inputmode="decimal" placeholder="г / 100 г" class="w-full h-10 px-3 rounded-xl bg-[#F5EFEA] text-15 text-ink outline-none mt-1 placeholder:text-ink placeholder:font-medium" />
                <p class="text-13 text-ink/70 mt-1 leading-snug">Не нашли — оставьте пустым, Зоя оценит сама.</p>
              </div>

              <div>
                <label class="text-14 text-ink/70 font-medium">Штрихкод</label>
                <p class="text-14 text-ink mt-1 px-3 py-2 rounded-xl bg-[#F5EFEA]/60">{{ scannedCode }}</p>
              </div>
              <div v-if="reviewData.ingredients_text">
                <label class="text-14 text-ink/70 font-medium">Состав</label>
                <p class="text-14 text-ink/70 mt-1 px-3 py-2 rounded-xl bg-[#F5EFEA]/60 leading-snug">{{ reviewData.ingredients_text }}</p>
              </div>
            </div>

            <!-- Отказ сервера — над кнопкой и в зоне видимости. Стоял под
                 кнопкой, за нижним краем экрана: человек жал «Добавить»,
                 кнопка мигала «Сохраняем…», а причины он не видел
                 (5.10.2026 — 51 нажатие подряд у одного пользователя). -->
            <p v-if="submitError" ref="submitErrorEl" role="alert"
               class="text-14 text-center rounded-xl px-3 py-2.5 leading-snug"
               style="color: var(--error); background: rgba(194, 65, 65, 0.08);">{{ submitError }}</p>

            <button
              @click="submitProduct"
              :disabled="submitting || !reviewData.name"
              class="w-full h-12 rounded-2xl text-white font-semibold text-16 transition-colors"
              :class="submitting ? 'bg-ink/50' : 'bg-ink active:bg-[#2E1D12]'"
            >
              {{ submitting ? 'Сохраняем...' : 'Добавить продукт в базу данных' }}
            </button>
          </div>

          <!-- ══ SAVING ══ -->
          <div v-else-if="state === 'saving'" class="flex flex-col items-center justify-center gap-4 py-12">
            <div class="w-10 h-10 border-3 border-ink/20 border-t-[#41291A] rounded-full animate-spin"></div>
            <p class="text-15 text-ink/70">Сохраняем продукт...</p>
          </div>

        </div>
        </template>
      </div>
    </div>

    <!-- Выбор производителя: один товар ВкусВилла делают несколько заводов,
         и КБЖУ у них разные — иногда в два раза. Молча подставлять один из
         вариантов нечестно, поэтому спрашиваем. -->
    <div v-if="variantDialogVisible" class="fixed inset-0 z-[130] flex items-end sm:items-center justify-center">
      <div class="absolute inset-0 bg-black/40"></div>
      <div class="relative w-full sm:max-w-md bg-cream rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" style="padding-bottom: calc(1.25rem + var(--sheet-bottom, 0px))">
        <p class="text-17 font-semibold text-ink leading-tight">{{ foundProduct?.name }}</p>
        <p class="text-15 text-ink/70 mt-2 leading-snug">
          У этого товара {{ variantsCountText }} в зависимости от производителя.
          Посмотрите на упаковку:
        </p>

        <div class="flex flex-col gap-2 mt-4">
          <button
            v-for="(v, i) in productVariants"
            :key="i"
            @click="applyVariant(v)"
            class="w-full text-left bg-white rounded-2xl p-3 shadow-sm active:bg-[#F5EFEA] transition-colors"
          >
            <p class="text-15 font-semibold text-ink">{{ v.supplier || 'Производитель не указан' }}</p>
            <div class="flex gap-2 flex-wrap mt-2">
              <span class="pill-kcal text-14 font-medium">{{ fmtVal(v.kcal) }} ккал</span>
              <span class="pill-protein text-14 font-medium">Б {{ fmtVal(v.protein) }}</span>
              <span class="pill-fat text-14 font-medium">Ж {{ fmtVal(v.fat) }}</span>
              <span class="pill-carbs text-14 font-medium">У {{ fmtVal(v.carbs) }}</span>
            </div>
          </button>
        </div>

        <button @click="applyAverageVariant" class="w-full mt-4 py-3 text-15 text-ink/70 underline">
          Не знаю — взять средний
        </button>
      </div>
    </div>

    <!-- Диалог коррекции продукта -->
    <LabelReviewDialog
      v-if="correctionDialogVisible"
      v-model="correctionDialogVisible"
      :initial-data="correctionDialogData"
      :original-product-id="foundProduct?.id"
      :start-in-review="true"
      :show-barcode-scanner="false"
      mode="correction"
      header-text="Исправление данных"
      @submitted="onCorrectionSubmitted"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { markUnsaved, clearUnsaved } from '../services/unsaved.js'
// Пока сканер и его форма открыты, автообновление не перезагружает страницу
const barcodeUnsavedKey = Symbol('BarcodeScannerDialog')
onMounted(() => { markUnsaved(barcodeUnsavedKey) })
onUnmounted(() => { clearUnsaved(barcodeUnsavedKey) })
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import api from '../services/api.js'
import { BARCODE_PHOTO_PROMPT_VERSION } from '../utils/promptVersions'
import { imgUrl } from '../utils/image.js'
import { startNetProbe } from '../utils/netdiag.js'
import { withAiJob } from '../utils/aiProgress.js'
import { useAuthStore } from '../store/auth.js'
import { track } from '../analytics.js'
import LabelReviewDialog from './LabelReviewDialog.vue'
import PhotoPicker from './PhotoPicker.vue'

const authStore = useAuthStore()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  eatenAt: { type: String, default: null },
  selectOnly: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'added', 'select'])

const state = ref('scanning') // scanning | searching | found | not-found | label-recognizing | review | saving
const scannedCode = ref('')
const scanError = ref('')
const foundProduct = ref(null)

// ── Выбор производителя ─────────────────────────────────────────────────────
// Записи дневника хранят КБЖУ у себя (POST /api/diary принимает числа), поэтому
// выбор варианта — это просто подмена чисел перед добавлением. Карточка продукта
// в общей базе не меняется: там лежит вариант, выбранный автоматически.
const variantDialogVisible = ref(false)
const productVariants = computed(() => foundProduct.value?.variants || [])
const variantsCountText = computed(() => {
  const n = productVariants.value.length
  const last = n % 10, tens = n % 100
  if (tens >= 11 && tens <= 14) return `${n} вариантов`
  if (last === 1) return `${n} вариант`
  if (last >= 2 && last <= 4) return `${n} варианта`
  return `${n} вариантов`
})

function applyVariant(v) {
  const p = foundProduct.value
  if (!p) return
  p.kcal = v.kcal ?? p.kcal
  p.protein = v.protein ?? p.protein
  p.fat = v.fat ?? p.fat
  p.carbs = v.carbs ?? p.carbs
  variantDialogVisible.value = false
  track('barcode_variant_picked', { code: scannedCode.value, supplier: v.supplier || null })
}

// «Не знаю» — единственный случай, когда среднее арифметическое уместно.
// В карточку продукта его писать нельзя (она обязана совпадать с реальной
// упаковкой), но для одной записи дневника при неизвестном заводе среднее
// ошибается меньше, чем любой наугад выбранный вариант.
function applyAverageVariant() {
  const list = productVariants.value
  const p = foundProduct.value
  if (!p || !list.length) return
  const avg = key => {
    const vals = list.map(v => v[key]).filter(x => x != null)
    if (!vals.length) return null
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
  }
  p.kcal = avg('kcal') ?? p.kcal
  p.protein = avg('protein') ?? p.protein
  p.fat = avg('fat') ?? p.fat
  p.carbs = avg('carbs') ?? p.carbs
  variantDialogVisible.value = false
  track('barcode_variant_averaged', { code: scannedCode.value, variants: list.length })
}
const portionWeight = ref(100)
const portionWeightStr = ref('100')
function onPortionWeightInput(e) {
  const raw = e.target.value.replace(',', '.')
  portionWeightStr.value = e.target.value
  const num = parseFloat(raw)
  portionWeight.value = isNaN(num) ? 0 : num
}
function parseDecimal(str) {
  const num = parseFloat(String(str).replace(',', '.'))
  return isNaN(num) ? 0 : num
}
const addingToDiary = ref(false)

// Review data
const reviewData = ref({ name: '', brand: '', kcal: null, protein: null, fat: null, carbs: null, fiber: null, ingredients_text: '' })
const fiberOpen = ref(false)
const submitting = ref(false)
const submitError = ref('')
const submitErrorEl = ref(null)

// Incomplete product (found but КБЖУ = 0)
const incompleteProductName = ref('')
const incompleteProductBrand = ref('')

// Внешняя база (Open Food Facts) недоступна — «не найден» тут означает
// «не смогли посмотреть», а не «продукта не существует»
const lookupUnavailable = ref(false)
const retryingLookup = ref(false)

// Label photo input
const labelPhotoUrl = ref(null)

// Cover photo
const coverBase64 = ref(null)
const coverMime = ref('image/jpeg')
const coverPreviewUrl = ref(null)
const coverPhotoUrl = ref(null) // S3 URL after upload
const coverUploading = ref(false)

// Manual input
const showManualInput = ref(false)
const manualCode = ref('')

// Photo-based barcode scanning (native camera, no permission prompt)
const barcodePhotoError = ref('')

// Focus hint (iOS)
const showFocusHint = ref(false)
let focusHintTimer = null
let refocusInterval = null

// Scanner
let scanner = null

const headerTitle = computed(() => {
  switch (state.value) {
    case 'scanning': return 'Сканирование'
    case 'searching': return 'Поиск...'
    case 'found': return 'Продукт найден'
    case 'not-found': return incompleteProductName.value ? 'Нет данных о КБЖУ' : 'Не найден'
    case 'label-recognizing': return 'Распознавание'
    case 'review': return 'Проверьте данные'
    case 'saving': return 'Сохранение'
    default: return 'Сканирование'
  }
})

function fmtVal(val) {
  if (val === null || val === undefined) return '0'
  const n = parseFloat(val)
  if (isNaN(n)) return '0'
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

function calcPortion(per100) {
  if (per100 == null) return null
  return Math.round(per100 * portionWeight.value / 100 * 10) / 10
}

// ── Scanner lifecycle ──

onMounted(() => {
  startScanner()
})

onUnmounted(() => {
  stopScanner()
})

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

// keepError: не затирать уже показанное сообщение (например, ошибку поиска)
// и не подменять его ошибкой камеры — иначе настоящая причина теряется.
async function startScanner({ keepError = false } = {}) {
  if (!keepError) scanError.value = ''

  // Контейнер живёт под v-if="state === 'scanning'" — после смены состояния
  // Vue ещё не успел его отрендерить, и конструктор Html5Qrcode упадёт.
  await nextTick()
  const el = document.getElementById('barcode-reader')
  if (!el) return
  el.innerHTML = ''

  try {
    scanner = new Html5Qrcode('barcode-reader')
    await scanner.start(
      { facingMode: 'environment' },
      {
        fps: 15,
        qrbox: (viewfinderWidth, viewfinderHeight) => ({
          width: Math.round(viewfinderWidth * 0.9),
          height: Math.round(viewfinderHeight * 0.5),
        }),
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
        ],
      },
      onScanSuccess,
      () => {} // ignore scan failure (no code detected yet)
    )

    // Применяем улучшения камеры: автофокус + zoom
    try {
      const video = document.querySelector('#barcode-reader video')
      const videoTrack = video?.srcObject?.getVideoTracks()[0]
      if (videoTrack) {
        const caps = videoTrack.getCapabilities?.()
        const advanced = []

        // Непрерывный автофокус (Android Chrome; iOS игнорирует)
        if (caps?.focusMode?.includes('continuous')) {
          advanced.push({ focusMode: 'continuous' })
        }

        // Zoom 2x — помогает iOS Pro выбрать основной объектив и сфокусироваться
        const hasZoom = caps?.zoom?.max > (caps?.zoom?.min || 1)
        const targetZoom = hasZoom ? Math.min(2.0, caps.zoom.max) : 0
        if (targetZoom > 0) {
          advanced.push({ zoom: targetZoom })
        }

        if (advanced.length > 0) {
          await videoTrack.applyConstraints({ advanced })
        }

        // iOS: периодический «пинок» фокуса — сбрасываем zoom на 1x и возвращаем 2x
        // Имитирует приближение/отдаление, заставляя камеру перефокусироваться
        if (isIOS && hasZoom) {
          refocusInterval = setInterval(async () => {
            if (state.value !== 'scanning') return
            try {
              await videoTrack.applyConstraints({ advanced: [{ zoom: 1.0 }] })
              await new Promise(r => setTimeout(r, 300))
              await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] })
            } catch { /* track may be stopped */ }
          }, 5000)
        }

        // На iOS показываем подсказку через 4 сек если штрихкод ещё не распознан
        if (isIOS) {
          focusHintTimer = setTimeout(() => {
            if (state.value === 'scanning') showFocusHint.value = true
          }, 4000)
        }
      }
    } catch { /* camera constraints not supported — OK */ }
  } catch (err) {
    console.error('Scanner start error:', err)
    if (!keepError) scanError.value = 'Не удалось запустить камеру. Проверьте разрешения.'
  }
}

async function stopScanner() {
  // Очищаем таймеры фокуса
  if (focusHintTimer) { clearTimeout(focusHintTimer); focusHintTimer = null }
  if (refocusInterval) { clearInterval(refocusInterval); refocusInterval = null }
  showFocusHint.value = false

  if (scanner) {
    // Explicitly stop all video tracks to release camera hardware
    try {
      const video = document.querySelector('#barcode-reader video')
      if (video?.srcObject) {
        video.srcObject.getTracks().forEach(t => t.stop())
        video.srcObject = null
      }
    } catch {}
    try {
      const s = scanner.getState?.()
      if (s === 2 /* SCANNING */ || s === undefined) {
        await scanner.stop()
      }
    } catch { /* already stopped */ }
    try { scanner.clear() } catch {}
    scanner = null
  }
}

function submitManualCode() {
  const code = manualCode.value.trim()
  if (code.length < 8) return
  onScanSuccess(code)
}

const barcodePhotoLoading = ref(false)

async function onBarcodePhoto(file) {
  if (!file) return
  barcodePhotoError.value = ''

  // 1. Пробуем локальное распознавание (быстрое, без сети)
  try {
    const tempScanner = new Html5Qrcode('barcode-photo-temp')
    const code = await tempScanner.scanFile(file, false)
    tempScanner.clear()
    onScanSuccess(code)
    return
  } catch { /* локальное не сработало — пробуем ИИ */ }

  // 2. Fallback: отправляем фото в Gemini для распознавания штрихкода
  barcodePhotoLoading.value = true
  try {
    const base64 = await resizeAndEncode(file, 1200)
    const code = await withAiJob(job => callGeminiBarcode(base64, 'image/jpeg', 2, job))
    if (code) {
      onScanSuccess(code)
    } else {
      barcodePhotoError.value = 'Штрихкод не распознан. Попробуйте ещё раз.'
    }
  } catch {
    barcodePhotoError.value = 'Ошибка распознавания. Попробуйте ещё раз.'
  } finally {
    barcodePhotoLoading.value = false
  }
}

async function onScanSuccess(code) {
  if (state.value !== 'scanning') return

  scannedCode.value = code
  track('barcode_scanned', { code })

  // Stop scanner BEFORE changing state — #barcode-reader must still be in DOM for proper cleanup
  await stopScanner()

  await lookupProduct(code)
}

// Вынесено из onScanSuccess, чтобы можно было повторить поиск тем же кодом
// без повторного сканирования и без задваивания аналитики.
async function lookupProduct(code) {
  state.value = 'searching'
  lookupUnavailable.value = false

  try {
    const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`)
    const p = data.product

    // Если КБЖУ все = 0 — продукт неполный, предлагаем сфотографировать этикетку
    if (!p.kcal && !p.protein && !p.fat && !p.carbs) {
      incompleteProductName.value = p.name || ''
      incompleteProductBrand.value = p.brand || ''
      state.value = 'not-found'
      track('barcode_found_incomplete', { code, name: p.name })
      return
    }

    foundProduct.value = p
    state.value = 'found'
    // Несколько производителей — спрашиваем сразу, до ввода веса: иначе человек
    // введёт порцию, а цифры под ней потом поменяются.
    variantDialogVisible.value = (p.variants?.length || 0) > 1
    track('barcode_found', { code, source: data.source, variants: p.variants?.length || 0 })
  } catch (err) {
    if (err.response?.status === 404) {
      // Внешняя база лежит — это НЕ «продукта не существует».
      // Не выдаём это за отсутствие продукта: иначе пользователь заведёт дубль,
      // который через ON CONFLICT (barcode) DO UPDATE навсегда перекроет запись OFF.
      lookupUnavailable.value = !!err.response?.data?.lookup_unavailable
      // Штрихкод известен внешней базе, но КБЖУ там нет — такую запись мы не
      // сохраняем. Говорим об этом прямо, иначе «нет в базе» выглядит странно
      // рядом с названием, которое мы явно откуда-то знаем.
      if (err.response?.data?.incomplete_source) {
        incompleteProductName.value = err.response.data.product_name || ''
        incompleteProductBrand.value = err.response.data.brand || ''
      }
      state.value = 'not-found'
      track('barcode_not_found', { code, lookupUnavailable: lookupUnavailable.value })
    } else {
      // Ошибка ПОИСКА, а не камеры. Сообщение должно пережить перезапуск сканера.
      scanError.value = err.response
        ? 'Не удалось найти продукт по штрихкоду. Сфотографируйте этикетку или введите код вручную.'
        : 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.'
      state.value = 'scanning'
      track('barcode_lookup_error', { code, status: err.response?.status || 0 })
      await startScanner({ keepError: true })
    }
  }
}

async function retryLookup() {
  if (!scannedCode.value || retryingLookup.value) return
  retryingLookup.value = true
  try {
    await lookupProduct(scannedCode.value)
  } finally {
    retryingLookup.value = false
  }
}

// ── Select product only (for ProductSearchModal / KnowledgeBase) ──

function selectAndClose() {
  const p = foundProduct.value
  if (!p) return
  emit('select', {
    id: p.id,
    name: p.name,
    kcal: p.kcal ?? null,
    protein: p.protein ?? null,
    fat: p.fat ?? null,
    carbs: p.carbs ?? null,
    fiber: p.fiber ?? null,
    water: p.water ?? null,
    source: p.source || 'barcode',
  })
  close()
}

// ── Add to diary ──

async function addToDiary() {
  if (!foundProduct.value || portionWeight.value <= 0) return
  addingToDiary.value = true
  try {
    const p = foundProduct.value

    // Если пользователь добавил фото упаковки для продукта без фото — сохраняем
    if (coverBase64.value && !p.image_url && p.id) {
      const imageUrl = await uploadCoverPhoto()
      if (imageUrl) {
        api.put(`/products/${p.id}/image`, { image_url: imageUrl }).catch(() => {})
        p.image_url = imageUrl
      }
    }

    // Создаём dish для возможности добавления в избранное
    let dishId = null
    try {
      const { data: dishData } = await api.post('/diary/dishes', {
        name: p.name,
        photo_url: p.image_url || null,
      })
      dishId = dishData.dish?.id
    } catch {}

    const { data } = await api.post('/diary', {
      product_id: p.id || null,
      product_name: p.name,
      weight_g: portionWeight.value,
      kcal: calcPortion(p.kcal),
      protein: calcPortion(p.protein),
      fat: calcPortion(p.fat),
      carbs: calcPortion(p.carbs),
      fiber: calcPortion(p.fiber) ?? null,
      water_ml: calcPortion(p.water) ?? null,
      eaten_at: props.eatenAt || new Date().toISOString(),
      source: 'barcode',
      dish_id: dishId,
    })
    track('food_added_barcode', { product_name: p.name, weight_g: portionWeight.value })
    // Добавляем данные для корректного отображения в дневнике
    if (p.image_url) data.entry.product_image_url = p.image_url
    if (dishId) {
      data.entry.dish_id = dishId
      data.entry.dish_name = p.name
      data.entry.is_favorite = false
    }
    emit('added', data.entry)
    close()
  } catch (err) {
    alert(err.response?.data?.error || 'Ошибка добавления')
  } finally {
    addingToDiary.value = false
  }
}

// ── Reset to scan ──

async function resetToScan() {
  // Ensure any lingering scanner is fully stopped
  await stopScanner()

  state.value = 'scanning'
  scannedCode.value = ''
  foundProduct.value = null
  variantDialogVisible.value = false
  portionWeight.value = 100
  portionWeightStr.value = '100'
  scanError.value = ''
  submitError.value = ''
  barcodePhotoError.value = ''
  incompleteProductName.value = ''
  incompleteProductBrand.value = ''
  lookupUnavailable.value = false
  coverBase64.value = null
  coverMime.value = 'image/jpeg'
  coverPreviewUrl.value = null
  coverPhotoUrl.value = null
  showManualInput.value = false
  manualCode.value = ''
  showFocusHint.value = false

  await nextTick()

  // Clean leftover content from the fresh DOM element
  const el = document.getElementById('barcode-reader')
  if (el) el.innerHTML = ''

  // Brief delay to let browser fully release camera hardware (iOS Safari)
  await new Promise(r => setTimeout(r, 400))

  await startScanner()
}

// ── Cover photo (упаковка) ──

async function onCoverPhoto(file) {
  if (!file) return

  coverPreviewUrl.value = URL.createObjectURL(file)
  coverBase64.value = await resizeAndEncode(file, 800)
  coverMime.value = file.type || 'image/jpeg'
}

async function uploadCoverPhoto() {
  if (!coverBase64.value) return null
  try {
    const { data } = await api.post('/diary/upload-photo', {
      base64: coverBase64.value,
      mimeType: coverMime.value,
    })
    return data.url
  } catch (err) {
    console.error('Cover photo upload error:', err)
    return null
  }
}

// ── Label photo ──

async function onLabelPhoto(file) {
  if (!file) return

  state.value = 'label-recognizing'

  try {
    const base64 = await resizeAndEncode(file, 1024)
    const mime = file.type || 'image/jpeg'
    labelPhotoUrl.value = `data:${mime};base64,${base64}`
    const result = await withAiJob(job => callGeminiLabel(base64, mime, 2, job))

    reviewData.value = {
      name: result.product_name || incompleteProductName.value || '',
      brand: result.brand || incompleteProductBrand.value || '',
      kcal: result.per100?.kcal ?? null,
      protein: result.per100?.protein ?? null,
      fat: result.per100?.fat ?? null,
      carbs: result.per100?.carbs ?? null,
      fiber: result.per100?.fiber ?? null,
      ingredients_text: result.ingredients_text || '',
    }
    // Распознанное значение нельзя прятать за свёрнутым полем
    if (reviewData.value.fiber != null) fiberOpen.value = true
    state.value = 'review'
  } catch (err) {
    console.error('Label recognition error:', err)
    scanError.value = err.message || 'Не удалось распознать этикетку'
    state.value = 'not-found'
  }
}

// ── Submit product ──

async function submitProduct() {
  if (!reviewData.value.name || submitting.value) return
  submitting.value = true
  submitError.value = ''

  try {
    // Загружаем фото упаковки в S3 (если есть)
    let imageUrl = null
    if (coverBase64.value) {
      imageUrl = await uploadCoverPhoto()
    }

    const { data } = await api.post('/products/user-submit', {
      barcode: scannedCode.value,
      name: reviewData.value.name,
      brand: reviewData.value.brand || null,
      per100: {
        kcal: reviewData.value.kcal,
        protein: reviewData.value.protein,
        fat: reviewData.value.fat,
        carbs: reviewData.value.carbs,
        fiber: reviewData.value.fiber,
      },
      ingredients_text: reviewData.value.ingredients_text || null,
      image_url: imageUrl,
    })

    track('product_submitted', { barcode: scannedCode.value, name: reviewData.value.name })

    // Показываем найденный продукт для добавления в дневник
    foundProduct.value = data.product
    portionWeight.value = 100
    portionWeightStr.value = '100'
    state.value = 'found'
  } catch (err) {
    submitError.value = err.response?.data?.error || 'Ошибка сохранения'
    await nextTick()
    submitErrorEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  } finally {
    submitting.value = false
  }
}

// ── Close ──

function close() {
  stopScanner()
  emit('update:modelValue', false)
}

// ── Коррекция продукта ──

const correctionDialogVisible = ref(false)

const correctionDialogData = computed(() => {
  const p = foundProduct.value
  if (!p) return null
  return {
    name: p.name,
    brand: p.brand || null,
    per100: { kcal: p.kcal, protein: p.protein, fat: p.fat, carbs: p.carbs, fiber: p.fiber },
    ingredients_text: p.ingredients_text || null,
  }
})

function openCorrectionDialog() {
  correctionDialogVisible.value = true
}

function onCorrectionSubmitted(result) {
  const p = result.product
  // Обновляем foundProduct на исправленные данные
  foundProduct.value = {
    ...foundProduct.value,
    id: p.id,
    name: p.name,
    source: p.source,
    brand: p.brand,
    image_url: p.image_url || foundProduct.value.image_url,
    ingredients_text: p.ingredients_text || foundProduct.value.ingredients_text,
    kcal: p.kcal,
    protein: p.protein,
    fat: p.fat,
    carbs: p.carbs,
  }
}

// ── Gemini barcode recognition ──

const BARCODE_PROMPT = `На фото — штрихкод продукта (EAN-13, EAN-8, UPC-A или другой формат).
Распознай числовой код штрихкода.

Верни СТРОГО JSON (без markdown):
{"barcode": "4600104036385"}

Правила:
- Верни только цифры штрихкода (обычно 8 или 13 цифр)
- Если штрихкод не виден или не читается — верни {"barcode": null}`

async function callGeminiBarcode(base64, mimeType, retries = 2, job = '') {
  let result
  try {
    const { data } = await api.post(`/ai/gemini-proxy?model=gemini-2.5-flash&label=/barcode/photo&pv=${BARCODE_PHOTO_PROMPT_VERSION}&job=${job}`, {
      contents: [{
        parts: [
          { text: BARCODE_PROMPT },
          { inline_data: { mime_type: mimeType, data: base64 } },
        ],
      }],
    }, { timeout: 60000 })
    result = data
  } catch (err) {
    // isAxiosError обязателен. Без него сюда попадала любая ошибка нашего же
    // кода, брошенная после успешного ответа: отсутствие err.response читалось
    // как «сети нет». Так опечатка в auth-store (вызов функции, переименованной
    // в другом коммите) три недели выглядела сетевым сбоем, трижды повторяла
    // запрос и жгла токены Gemini на ответах, которые уже пришли.
    const isNetwork = !err.response && err.isAxiosError === true
    const is429 = err.response?.status === 429
    if (retries > 0 && (is429 || isNetwork)) {
      await new Promise(r => setTimeout(r, isNetwork ? 2000 : 2000))
      return callGeminiBarcode(base64, mimeType, retries - 1, job)
    }
    if (isNetwork) {
      throw new Error('Не дождались ответа — попробуйте ещё раз')
    }
    throw new Error(err.response?.data?.error?.message || err.message || 'Gemini error')
  }

  // Расход токенов считает сервер по ответу Gemini (метка — в query
  // запроса выше); здесь только обновление индикатора бюджета.
  authStore.fetchTokenBudget()

  const text = result.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || ''
  const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()

  try {
    const parsed = JSON.parse(cleaned)
    const code = parsed.barcode
    if (code && /^\d{8,14}$/.test(code)) return code
    return null
  } catch {
    return null
  }
}

// ── Gemini label recognition ──

async function callGeminiLabel(base64, mimeType, retries = 2, job = '') {
  // Промпт и вызов Gemini живут на бэкенде (POST /api/ai/recognize-label):
  // раньше текст промпта был продублирован в двух компонентах и уезжал
  // в бандл. Здесь остаётся только картинка и обработка ответа.
  const probe = startNetProbe()
  try {
    const { data } = await api.post('/ai/recognize-label', {
      image: base64,
      mime_type: mimeType,
      job,
    }, { timeout: 120000 })

    probe.done()
    if (data.budget?.pct != null) authStore.updateTokenBudget(data.budget)
    return data.result
  } catch (err) {
    // isAxiosError обязателен. Без него сюда попадала любая ошибка нашего же
    // кода, брошенная после успешного ответа: отсутствие err.response читалось
    // как «сети нет». Так опечатка в auth-store (вызов функции, переименованной
    // в другом коммите) три недели выглядела сетевым сбоем, трижды повторяла
    // запрос и жгла токены Gemini на ответах, которые уже пришли.
    const isNetwork = !err.response && err.isAxiosError === true
    const is429 = err.response?.status === 429
    // Сервер отвечает 200 с телом, а сюда ответ не доходит — собрать с
    // устройства то, чего не видно с сервера. Разбор 27 августа 2026.
    // Сообщаем и о сбое сети, и об исключении своего кода: ответа нет ни в
    // том, ни в другом случае, а отличить их можно только по коду в отчете.
    // Повторяем при этом лишь сетевые — см. isNetwork выше.
    if (!err.response) probe.lost('/ai/recognize-label', err, 3 - retries)
    else probe.done()
    if (retries > 0 && (is429 || isNetwork)) {
      await new Promise(r => setTimeout(r, 3000))
      return callGeminiLabel(base64, mimeType, retries - 1, job)
    }
    if (isNetwork) {
      throw new Error('Не дождались ответа — попробуйте ещё раз')
    }
    throw new Error(err.response?.data?.error || err.message || 'Не удалось распознать этикетку')
  }
}

// ── Resize image ──

function resizeAndEncode(file, maxPx) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      resolve(dataUrl.split(',')[1])
    }
    img.onerror = reject
    img.src = url
  })
}
</script>

<style scoped>
.pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); }

.scan-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(201,122,63,0.2) 50%, transparent 100%);
  background-size: 100% 30%;
  background-repeat: no-repeat;
  animation: scan 2.4s ease-in-out infinite;
}
@keyframes scan {
  0%   { background-position: 0 -30%; }
  50%  { background-position: 0 130%; }
  100% { background-position: 0 -30%; }
}
.scan-corner {
  position: absolute;
  width: 18px;
  height: 18px;
}

/* html5-qrcode overrides */
:deep(#barcode-reader) {
  border: none !important;
}
:deep(#barcode-reader video) {
  border-radius: 16px;
  object-fit: cover;
}
:deep(#barcode-reader__scan_region) {
  min-height: 200px;
}
:deep(#barcode-reader__dashboard) {
  display: none !important;
}
</style>
